<template>
  <el-dialog
    v-model="dialogVisible"
    title="资料详情"
    width="920px"
    destroy-on-close
    @close="handleClose"
  >
    <div v-loading="loading" class="space-y-4">
      <template v-if="materialDetail">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div class="rounded-lg bg-slate-50 p-3">
            <div class="text-xs text-slate-500">资料名称</div>
            <div class="mt-1 text-sm font-medium text-slate-900">{{ materialDetail.name || '-' }}</div>
          </div>
          <div class="rounded-lg bg-slate-50 p-3">
            <div class="text-xs text-slate-500">资料类型</div>
            <div class="mt-1 text-sm font-medium text-slate-900">{{ materialDetail.definitionName || materialDetail.definitionCode || '-' }}</div>
          </div>
          <div class="rounded-lg bg-slate-50 p-3">
            <div class="text-xs text-slate-500">客户可见性</div>
            <div class="mt-1 text-sm font-medium text-slate-900">{{ materialDetail.visibleToCustomer ? '对客户可见' : '对客户隐藏' }}</div>
          </div>
          <div class="rounded-lg bg-slate-50 p-3">
            <div class="text-xs text-slate-500">最后更新时间</div>
            <div class="mt-1 text-sm font-medium text-slate-900">{{ $formatDateTime(materialDetail.updateTime || materialDetail.lastUploadTime || materialDetail.createTime) }}</div>
          </div>
        </div>

        <div v-if="materialDetail.expireDate" class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          有效期截止：{{ $formatDateTime(materialDetail.expireDate) }}
        </div>

        <div>
          <div class="mb-2 text-sm font-medium text-slate-700">表单内容</div>
          <div v-if="formEntries.length" class="grid gap-3 md:grid-cols-2">
            <div
              v-for="entry in formEntries"
              :key="entry.key"
              class="rounded-lg border border-slate-200 p-3"
            >
              <div class="text-xs text-slate-500">{{ entry.key }}</div>
              <div class="mt-1 whitespace-pre-wrap break-all text-sm text-slate-900">{{ entry.value }}</div>
            </div>
          </div>
          <div v-else class="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
            暂无表单内容
          </div>
        </div>

        <div>
          <div class="mb-2 text-sm font-medium text-slate-700">文件列表</div>
          <div v-if="materialFiles.length" class="space-y-3">
            <div
              v-for="file in materialFiles"
              :key="file.id"
              class="flex flex-col gap-3 rounded-lg border border-slate-200 p-3 md:flex-row md:items-center md:justify-between"
            >
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-medium text-slate-900">{{ file.originalName || '-' }}</div>
                <div class="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
                  <span>字段：{{ file.fieldName || file.fieldCode || '-' }}</span>
                  <span>版本：{{ file.version || '-' }}</span>
                  <span>上传时间：{{ $formatDateTime(file.uploadTime) }}</span>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <button class="table-action-button" @click="handlePreviewMaterialFile(file)">预览</button>
                <button class="table-action-button is-primary" @click="handleDownloadMaterialFile(file)">下载</button>
              </div>
            </div>
          </div>
          <div v-else class="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
            暂无文件
          </div>
        </div>
      </template>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <button class="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" @click="handleClose">关闭</button>
      </div>
    </template>
  </el-dialog>

  <el-dialog
    v-model="showPreviewDialog"
    title="文件预览"
    width="960px"
    destroy-on-close
    @close="closePreviewDialog"
  >
    <div v-loading="previewLoading" class="min-h-[480px]">
      <iframe
        v-if="previewType === 'pdf' && previewUrl"
        :src="previewUrl"
        class="h-[70vh] w-full rounded-lg border border-slate-200"
      />
      <img
        v-else-if="previewType === 'image' && previewUrl"
        :src="previewUrl"
        alt=""
        class="mx-auto max-h-[70vh] rounded-lg"
      >
      <div v-else class="flex h-[480px] items-center justify-center text-sm text-slate-500">
        当前文件不支持预览，请下载查看
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import axios from 'axios'
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { getCustomerMaterialDetailById } from '@/api/customer'
import { useUserStore } from '@/stores/user'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  userMaterialId: {
    type: [Number, String],
    default: null
  }
})

const emit = defineEmits(['update:visible'])

const loading = ref(false)
const materialDetail = ref(null)
const showPreviewDialog = ref(false)
const previewLoading = ref(false)
const previewUrl = ref('')
const previewType = ref('')

const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

const materialFiles = computed(() => materialDetail.value?.files || [])

const formEntries = computed(() => {
  const parsed = parseJsonSafely(materialDetail.value?.formData, {})
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return []
  }
  return Object.entries(parsed).map(([key, value]) => ({
    key,
    value: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '-')
  }))
})

watch(() => [props.visible, props.userMaterialId], async ([visible, userMaterialId]) => {
  if (!visible || !userMaterialId) {
    return
  }
  loading.value = true
  try {
    materialDetail.value = await getCustomerMaterialDetailById(userMaterialId)
  } catch (error) {
    ElMessage.error(error?.message || '获取资料详情失败')
  } finally {
    loading.value = false
  }
}, { immediate: true })

const parseJsonSafely = (value, fallback) => {
  if (value === null || value === undefined || value === '') {
    return fallback
  }
  if (typeof value === 'object') {
    return value
  }
  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback
  }
}

const resolveMaterialUrl = (url) => {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  const baseUrl = process.env.VUE_APP_API_BASE_URL || ''
  if (!baseUrl) {
    return url.startsWith('/') ? url : `/${url}`
  }
  return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`
}

const getMaterialPreviewUrl = (file) => {
  return resolveMaterialUrl(file?.previewUrl || `/api/customer/materials/files/${file?.id}/preview`)
}

const getMaterialDownloadUrl = (file) => {
  return resolveMaterialUrl(file?.downloadUrl || file?.fileUrl || `/api/customer/materials/files/${file?.id}/download`)
}

const getMaterialFileExtension = (file) => {
  if (file?.extension) return String(file.extension).toLowerCase()
  const name = file?.originalName || ''
  const match = name.match(/\.([0-9a-z]+)$/i)
  return match ? match[1].toLowerCase() : ''
}

const previewableExtensions = new Set(['pdf', 'png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'tiff'])

const getAuthHeaders = () => {
  const userStore = useUserStore()
  const headers = {}
  if (userStore.accessToken) {
    headers.Authorization = `${userStore.tokenType} ${userStore.accessToken}`
  }
  let deviceId = localStorage.getItem('deviceId')
  if (!deviceId) {
    deviceId = `WEB-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    localStorage.setItem('deviceId', deviceId)
  }
  headers['X-Device-Id'] = deviceId
  headers['X-Client-Type'] = 'web'
  return headers
}

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename || 'download'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const handlePreviewMaterialFile = async (file) => {
  const extension = getMaterialFileExtension(file)
  if (!previewableExtensions.has(extension)) {
    ElMessage.warning('该文件类型不支持预览，请下载查看')
    return
  }
  previewLoading.value = true
  showPreviewDialog.value = true
  try {
    const response = await axios.get(getMaterialPreviewUrl(file), {
      responseType: 'blob',
      headers: getAuthHeaders()
    })
    if (previewUrl.value) {
      URL.revokeObjectURL(previewUrl.value)
    }
    previewUrl.value = URL.createObjectURL(response.data)
    previewType.value = extension === 'pdf' ? 'pdf' : 'image'
  } catch (error) {
    ElMessage.error(error?.message || '预览失败，请稍后重试')
  } finally {
    previewLoading.value = false
  }
}

const handleDownloadMaterialFile = async (file) => {
  try {
    const response = await axios.get(getMaterialDownloadUrl(file), {
      responseType: 'blob',
      headers: getAuthHeaders()
    })
    downloadBlob(response.data, file?.originalName)
  } catch (error) {
    ElMessage.error(error?.message || '下载失败，请稍后重试')
  }
}

const closePreviewDialog = () => {
  showPreviewDialog.value = false
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
  }
  previewUrl.value = ''
  previewType.value = ''
}

const handleClose = () => {
  emit('update:visible', false)
}
</script>
